import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, ScanLine, Upload, X } from 'lucide-react';
import axiosInstance from '../../utils/axiosConfig';
import CierreFinal from './CierreFinal';

const formatMoney = (n) =>
    Number(n || 0).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const SIN_CLASIFICAR = '__sin__';

const COLOR_VERIFICACION = {
    correcto: 'text-green-700',
    liquidacion: 'text-green-700',
    incoherente: 'text-red-700',
};

const colorCategoria = (key) => {
    if (!key) return 'border-gray-200 text-gray-500 bg-white';
    if (key === 'conforme' || key === 'liquidacion') return 'border-green-200 text-green-800 bg-green-50';
    if (key === 'sin_conciliar' || key === 'liquidacion_pendiente') return 'border-amber-200 text-amber-800 bg-amber-50';
    return 'border-red-200 text-red-800 bg-red-50';
};

/**
 * Cierre de la venta: Costos clasifica cada pedido con la categoria que antes
 * Tesoreria escribia a mano en el Excel de pagos. La categoria sale sola en la
 * columna "Cierre" de ese Excel.
 *
 * soloLectura: la venta entera esta cerrada (boton "Cerrar venta"); se ve todo
 * pero no se puede tocar nada, ni reabrir el cierre.
 */
const CierreTab = ({ ventaId, soloLectura = false }) => {
    const [filas, setFilas] = useState([]);
    const [categorias, setCategorias] = useState([]);
    const [manuales, setManuales] = useState([]);
    const [venta, setVenta] = useState(null);
    const [resumen, setResumen] = useState(null);
    const [loading, setLoading] = useState(true);
    const [leyendo, setLeyendo] = useState(false);
    const [error, setError] = useState('');
    const [filtro, setFiltro] = useState(null);
    const [guardando, setGuardando] = useState(null);
    const [extractos, setExtractos] = useState([]);       // File[] elegidos, aun sin procesar
    const [conciliando, setConciliando] = useState(false);
    const [conciliacion, setConciliacion] = useState(null); // resultado de la ultima corrida
    const inputExtracto = useRef(null);

    const aplicar = (data) => {
        setFilas(data.filas || []);
        setCategorias(data.categorias || []);
        setManuales(data.categoriasManuales || []);
        setVenta(data.venta || null);
        setResumen(data.resumen || null);
    };

    const cargar = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const r = await axiosInstance.get(`/api/ventas-especiales/${ventaId}/cierre`);
            aplicar(r.data);
        } catch (e) {
            setError(e.response?.data?.error || 'No se pudo cargar el cierre');
        } finally {
            setLoading(false);
        }
    }, [ventaId]);

    useEffect(() => { cargar(); }, [cargar]);

    const leerComprobantes = async () => {
        setLeyendo(true);
        setError('');
        try {
            // Sin timeout: la IA lee las capturas de a pocas y una venta grande tarda.
            const r = await axiosInstance.post(`/api/ventas-especiales/${ventaId}/cierre/leer-pagos`, {}, { timeout: 0 });
            aplicar(r.data);
        } catch (e) {
            setError(e.response?.data?.error || 'No se pudieron leer los comprobantes');
        } finally {
            setLeyendo(false);
        }
    };

    const agregarExtractos = (lista) => {
        const nuevos = Array.from(lista || []);
        setExtractos(prev => [...prev, ...nuevos.filter(n => !prev.some(p => p.name === n.name && p.size === n.size))]);
    };

    const conciliar = async () => {
        setConciliando(true);
        setError('');
        try {
            const form = new FormData();
            extractos.forEach(f => form.append('extractos', f));
            const r = await axiosInstance.post(`/api/ventas-especiales/${ventaId}/cierre/conciliar`, form, { timeout: 0 });
            aplicar(r.data);
            setConciliacion(r.data.conciliacion);
            setExtractos([]);
        } catch (e) {
            setError(e.response?.data?.error || 'No se pudo conciliar el extracto');
        } finally {
            setConciliando(false);
        }
    };

    const pedirLiquidacion = async (f) => {
        if (!window.confirm(`${f.empleadoNombre} egresó sin pagar este pedido. ¿Pedir a GH que confirme el descuento en su liquidación?`)) return;
        try {
            await axiosInstance.post(`/api/ventas-especiales/pedidos/${f.pedidoId}/liquidacion/solicitar`);
            cargar();
        } catch (e) {
            setError(e.response?.data?.error || 'No se pudo enviar la solicitud a GH');
        }
    };

    const refrescarResumen = async () => {
        try {
            const r = await axiosInstance.get(`/api/ventas-especiales/${ventaId}/cierre`);
            setResumen(r.data.resumen);
        } catch (e) { /* el resumen se actualiza en la proxima carga */ }
    };

    const guardar = async (pedidoId, cambios) => {
        setGuardando(pedidoId);
        setError('');
        try {
            const r = await axiosInstance.put(`/api/ventas-especiales/pedidos/${pedidoId}/cierre`, cambios);
            setFilas(prev => prev.map(f => f.pedidoId === pedidoId ? r.data : f));
            if ('categoria' in cambios) refrescarResumen();
        } catch (e) {
            setError(e.response?.data?.error || 'No se pudo guardar el cambio');
        } finally {
            setGuardando(null);
        }
    };

    const labelDe = useMemo(() => Object.fromEntries(categorias.map(c => [c.key, c.label])), [categorias]);

    const conteos = useMemo(() => {
        const c = {};
        filas.forEach(f => {
            const k = f.cierreCategoria || SIN_CLASIFICAR;
            c[k] = (c[k] || 0) + 1;
        });
        return c;
    }, [filas]);

    const conComprobante = filas.filter(f => f.comprobantePago);
    const porLeer = conComprobante.filter(f => f.verificacion.estado === 'sin_leer' && f.verificacion.texto === 'Comprobante sin leer').length;
    const visibles = filtro === null ? filas : filas.filter(f => (f.cierreCategoria || SIN_CLASIFICAR) === filtro);

    // Sellado del cierre o venta cerrada: en ambos casos la pestana no se edita.
    const cerrada = !!venta?.cerrada || soloLectura;

    if (loading) {
        return <div className="flex items-center gap-2 py-16 justify-center text-sm text-gray-500"><Loader2 size={16} className="animate-spin" /> Cargando cierre</div>;
    }

    const chips = [
        { key: null, label: 'Todos', n: filas.length },
        { key: SIN_CLASIFICAR, label: 'Sin clasificar', n: conteos[SIN_CLASIFICAR] || 0 },
        ...categorias.filter(c => conteos[c.key]).map(c => ({ key: c.key, label: c.label, n: conteos[c.key] })),
    ];

    return (
        <div>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <p className="text-sm text-gray-600">
                    {conComprobante.length} de {filas.length} pedidos tienen comprobante
                    {porLeer > 0 ? `; ${porLeer} sin leer todavía.` : '.'}
                </p>
                <button
                    onClick={leerComprobantes}
                    disabled={leyendo || cerrada}
                    title="La IA lee fecha, referencia, banco y monto de cada captura que falte"
                    className="flex items-center gap-2 px-4 py-2 bg-violet-50 border border-violet-200 text-violet-700 rounded-lg text-sm hover:bg-violet-100 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                    {leyendo ? <><Loader2 size={16} className="animate-spin" /> Leyendo comprobantes</> : <><ScanLine size={16} /> Leer comprobantes</>}
                </button>
            </div>

            {cerrada && !soloLectura && (
                <p className="mb-4 rounded-xl bg-green-50 text-green-800 text-sm px-4 py-3">
                    El cierre de esta venta está sellado y en solo lectura. Para corregir algo, reábrelo al final de la página.
                </p>
            )}

            <div
                hidden={cerrada}
                className="mb-4 rounded-xl border border-dashed border-gray-300 bg-gray-50/60 px-4 py-3"
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); agregarExtractos(e.dataTransfer.files); }}
            >
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <p className="text-sm text-gray-600 max-w-xl">
                        Sube el extracto del banco (Venezolano de Crédito o BNC, en Excel) y el sistema
                        ubica el abono de cada pago y propone la categoría. El archivo no se guarda.
                    </p>
                    <div className="flex items-center gap-2">
                        <input ref={inputExtracto} type="file" accept=".xlsx" multiple className="hidden"
                            onChange={(e) => { agregarExtractos(e.target.files); e.target.value = ''; }} />
                        <button onClick={() => inputExtracto.current?.click()} disabled={conciliando}
                            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg text-sm hover:bg-gray-50">
                            <Upload size={16} /> Elegir extracto
                        </button>
                        <button onClick={conciliar} disabled={conciliando || extractos.length === 0}
                            className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg text-sm hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed">
                            {conciliando && <Loader2 size={16} className="animate-spin" />} Conciliar
                        </button>
                    </div>
                </div>
                {extractos.length > 0 && (
                    <ul className="mt-3 flex flex-wrap gap-2">
                        {extractos.map(f => (
                            <li key={f.name + f.size} className="flex items-center gap-1.5 pl-3 pr-1.5 py-1 text-xs bg-white border border-gray-200 rounded-full text-gray-700 max-w-full">
                                <span className="truncate max-w-[260px]">{f.name}</span>
                                <button onClick={() => setExtractos(prev => prev.filter(x => x !== f))} className="p-0.5 text-gray-400 hover:text-gray-700" aria-label={`Quitar ${f.name}`}><X size={12} /></button>
                            </li>
                        ))}
                    </ul>
                )}
                {conciliacion && (
                    <p className="mt-3 text-sm text-gray-700">
                        {conciliacion.extractos.map(e => `${e.banco}: ${e.movimientos} movimientos del ${e.desde} al ${e.hasta}`).join('. ')}.
                        {' '}Cruzaron {conciliacion.cruzados} pagos
                        {conciliacion.por_monto > 0 ? ` (${conciliacion.por_monto} solo por monto: revísalos)` : ''}
                        {conciliacion.sin_cruzar > 0 ? ` y ${conciliacion.sin_cruzar} no aparecen en el extracto.` : '.'}
                    </p>
                )}
            </div>

            {error && <p className="mb-3 text-sm text-red-600">{error}</p>}

            <div className="flex flex-wrap gap-2 mb-4">
                {chips.map(chip => {
                    const activo = filtro === chip.key;
                    return (
                        <button
                            key={chip.key ?? 'todos'}
                            onClick={() => setFiltro(chip.key)}
                            className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${activo ? 'bg-gray-900 border-gray-900 text-white' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                        >
                            {chip.label} <span className={`ml-1 tabular-nums ${activo ? 'text-gray-300' : 'text-gray-400'}`}>{chip.n}</span>
                        </button>
                    );
                })}
            </div>

            {filas.length === 0 ? (
                <p className="py-12 text-center text-sm text-gray-500">Esta venta todavía no tiene pedidos.</p>
            ) : (
                <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
                    <table className="w-full min-w-[1080px] text-sm">
                        <thead className="bg-gray-50 border-b border-gray-200">
                            <tr className="text-xs font-semibold text-gray-600">
                                <th className="text-left px-4 py-3">Empleado</th>
                                <th className="text-left px-3 py-3">Comprobante</th>
                                <th className="text-right px-3 py-3">Pagado (Bs)</th>
                                <th className="text-right px-3 py-3">A pagar (Bs)</th>
                                <th className="text-right px-3 py-3">Diferencia</th>
                                <th className="text-left px-3 py-3">Verificación</th>
                                <th className="text-left px-3 py-3">Cierre</th>
                                <th className="text-left px-3 py-3">Abono en el banco</th>
                                <th className="text-left px-3 py-3">Nota</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {visibles.map(f => {
                                const v = f.verificacion;
                                const esLiquidacion = (f.cierreCategoria || '').startsWith('liquidacion');
                                const puedeLiquidar = !cerrada && !f.cierreCategoria && !f.comprobantePago && f.empleadoActivo === false
                                    && !['pagado', 'completado'].includes(f.estado);
                                const ajustada = f.cierreSugerida && f.cierreCategoria && f.cierreSugerida !== f.cierreCategoria;
                                return (
                                    <tr key={f.pedidoId} className="hover:bg-gray-50/60">
                                        <td className="px-4 py-2.5 font-medium text-gray-900">
                                            {f.empleadoNombre}
                                            {f.empleadoActivo === false && <span className="ml-2 text-xs font-normal text-gray-400">egresado</span>}
                                        </td>
                                        <td className="px-3 py-2.5 text-xs text-gray-600 max-w-[200px]">
                                            {(f.pagoFecha || f.pagoReferencia) && (
                                                <>
                                                    {f.pagoFecha}{f.pagoFecha && f.pagoReferencia ? ', ' : ''}
                                                    {f.pagoReferencia && <>ref. <span className="tabular-nums">{f.pagoReferencia}</span></>}
                                                    <span className="block truncate text-gray-400" title={f.pagoBanco || ''}>{f.pagoBanco || ''}</span>
                                                </>
                                            )}
                                        </td>
                                        <td className="px-3 py-2.5 text-right tabular-nums text-gray-900">{v.pagado !== null ? formatMoney(v.pagado) : ''}</td>
                                        <td className="px-3 py-2.5 text-right tabular-nums text-gray-900">{formatMoney(v.debia)}</td>
                                        <td className={`px-3 py-2.5 text-right tabular-nums ${v.diferencia ? 'text-red-700 font-medium' : 'text-gray-400'}`}>
                                            {v.diferencia !== null ? formatMoney(v.diferencia) : ''}
                                        </td>
                                        <td className={`px-3 py-2.5 ${COLOR_VERIFICACION[v.estado] || 'text-gray-500'}`}>{v.texto}</td>
                                        <td className="px-3 py-2.5">
                                            {esLiquidacion ? (
                                                <span className={`inline-block px-2 py-1 text-xs border rounded-lg ${colorCategoria(f.cierreCategoria)}`} title="Lo resuelve GH desde su notificación">
                                                    {labelDe[f.cierreCategoria]}
                                                </span>
                                            ) : puedeLiquidar ? (
                                                <button onClick={() => pedirLiquidacion(f)}
                                                    className="px-2 py-1.5 text-xs text-amber-700 bg-white border border-amber-300 rounded-lg hover:bg-amber-50"
                                                    title="Egresó sin pagar. GH confirma si se le descontó en la liquidación.">
                                                    Pedir liquidación
                                                </button>
                                            ) : (
                                                <select
                                                    value={f.cierreCategoria || ''}
                                                    disabled={guardando === f.pedidoId || cerrada}
                                                    onChange={(e) => guardar(f.pedidoId, { categoria: e.target.value })}
                                                    aria-label={`Categoría de cierre de ${f.empleadoNombre}`}
                                                    className={`w-44 text-xs border rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-teal-500 ${colorCategoria(f.cierreCategoria)}`}
                                                >
                                                    <option value="">Sin clasificar</option>
                                                    {manuales.map(k => <option key={k} value={k}>{labelDe[k]}</option>)}
                                                </select>
                                            )}
                                            {ajustada && (
                                                <span className="block mt-1 text-[11px] text-gray-500" title="La pusiste a mano; el sistema no la cambia al reconciliar">
                                                    Sugerida: {labelDe[f.cierreSugerida]}
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2.5 text-xs text-gray-600 max-w-[230px]">
                                            {f.banco ? (
                                                <>
                                                    <span className="text-gray-900">{f.banco.cuenta}</span>, {f.banco.fecha}, ref. <span className="tabular-nums">{f.banco.referencia}</span>
                                                    <span className="block truncate text-gray-400" title={f.banco.descripcion}>{f.banco.descripcion}</span>
                                                </>
                                            ) : ''}
                                        </td>
                                        <td className="px-3 py-2.5">
                                            <input
                                                type="text"
                                                defaultValue={f.cierreNota || ''}
                                                disabled={cerrada}
                                                key={`${f.pedidoId}-${f.cierreNota || ''}`}
                                                onBlur={(e) => { if (e.target.value.trim() !== (f.cierreNota || '')) guardar(f.pedidoId, { nota: e.target.value }); }}
                                                onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                                                placeholder={cerrada ? '' : 'Agregar nota'}
                                                aria-label={`Nota de cierre de ${f.empleadoNombre}`}
                                                className="w-36 text-xs border border-transparent hover:border-gray-200 focus:border-gray-300 rounded-lg px-2 py-1.5 bg-transparent focus:bg-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                                            />
                                        </td>
                                    </tr>
                                );
                            })}
                            {visibles.length === 0 && (
                                <tr><td colSpan="9" className="px-4 py-10 text-center text-sm text-gray-500">Ningún pedido en esta categoría.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {venta && resumen && filas.length > 0 && (
                <CierreFinal ventaId={ventaId} venta={venta} resumen={resumen} filas={filas} onData={aplicar} soloLectura={soloLectura} />
            )}
        </div>
    );
};

export default CierreTab;
